import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, ClipboardList, Users, Mail, Crown, Ticket, ConciergeBell,
  Sparkles, PartyPopper, CheckSquare, Wrench, BarChart3, Settings, QrCode, ScrollText, DoorOpen,
  Shield, BookOpen, LogOut, LogIn, Search, Home, Menu, X, AlertTriangle, Radar, Image, Tv, Bot, Activity, UserPlus, Share2, BellRing, CalendarClock, CreditCard,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LogoMark } from "@/components/brand";
import GhostLauncher from "@/components/ghost/GhostLauncher";
import api from "@/lib/api";

const GROUPS = [
  { title: "Operations", links: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/ghost", label: "D", icon: Bot },
    { to: "/admin/calendar", label: "Calendar", icon: CalendarDays },
    { to: "/admin/reservations", label: "Reservations", icon: ClipboardList },
    { to: "/admin/in-house", label: "Currently at The D", icon: Radar },
    { to: "/admin/frontdesk", label: "Front Desk", icon: LogIn },
    { to: "/admin/payments", label: "Payment Requests", icon: CreditCard },
    { to: "/admin/permissions-matrix", label: "Permissions Matrix", icon: Shield, ownerOnly: true },
    { to: "/admin/rooms", label: "Rooms & Readiness", icon: DoorOpen },
    { to: "/admin/housekeeping", label: "Housekeeping", icon: Sparkles },
    { to: "/admin/operations", label: "Operations Board", icon: LayoutDashboard },
    { to: "/admin/schedule", label: "Staff Schedule", icon: CalendarDays },
    { to: "/admin/my-schedule", label: "My Schedule", icon: CalendarClock },
    { to: "/admin/concierge", label: "Concierge", icon: ConciergeBell },
    { to: "/admin/tasks", label: "Tasks", icon: CheckSquare },
    { to: "/admin/maintenance", label: "Maintenance", icon: Wrench },
    { to: "/admin/incidents", label: "Incidents", icon: AlertTriangle },
    { to: "/admin/events", label: "Private Events", icon: PartyPopper },
  ]},
  { title: "Guests", links: [
    { to: "/admin/guests", label: "Guests", icon: Users },
    { to: "/admin/invitations", label: "Invitations", icon: Ticket },
    { to: "/admin/referrals", label: "Referral Tracking", icon: Share2 },
    { to: "/admin/memberships", label: "Memberships", icon: Crown },
    { to: "/admin/guestbook", label: "Guestbook", icon: BookOpen },
  ]},
  { title: "Catalog & Comms", links: [
    { to: "/admin/experiences", label: "Experiences", icon: Sparkles },
    { to: "/admin/communications", label: "Communications", icon: Mail },
    { to: "/admin/qr", label: "QR Codes", icon: QrCode },
    { to: "/admin/house-guide", label: "House Guide", icon: BookOpen },
    { to: "/admin/gallery", label: "Gallery", icon: Image },
    { to: "/admin/display", label: "Welcome Screen", icon: Tv },
  ]},
  { title: "Admin", links: [
    { to: "/admin/reports", label: "Reports", icon: BarChart3 },
    { to: "/admin/audit", label: "Audit Log", icon: ScrollText },
    { to: "/admin/staff", label: "Staff & Teams", icon: Users },
    { to: "/admin/users", label: "Users & Roles", icon: Shield },
    { to: "/admin/access-requests", label: "Access Requests", icon: UserPlus },
    { to: "/admin/access-reminders", label: "Access Reminders", icon: BellRing },
    { to: "/admin/ghost-settings", label: "D Settings", icon: Bot },
    { to: "/admin/system", label: "System Status", icon: Activity },
    { to: "/admin/settings", label: "Configuration", icon: Settings },
  ]},
];

function SearchBox() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const nav = useNavigate();
  const run = async (v) => {
    setQ(v);
    if (v.length < 2) { setRes(null); return; }
    try { const { data } = await api.get("/admin/search", { params: { q: v } }); setRes(data); } catch {}
  };
  return (
    <div className="relative">
      <div className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-md px-3 py-2 w-72">
        <Search size={15} className="text-muted-foreground" />
        <input value={q} onChange={(e) => run(e.target.value)} placeholder="Search reservations, guests…" className="bg-transparent text-sm outline-none w-full" data-testid="admin-search" />
      </div>
      {res && (q.length >= 2) && (
        <div className="absolute mt-2 w-80 bg-surface border border-white/10 rounded-lg p-2 z-50 max-h-96 overflow-auto">
          {[["reservations", "/admin/reservations/"], ["guests", "/admin/guests"], ["invitations", "/admin/invitations"]].map(([key, base]) => (
            res[key]?.length > 0 && (
              <div key={key} className="mb-2">
                <p className="text-[10px] tracking-label uppercase text-muted-foreground px-2 py-1">{key}</p>
                {res[key].map((r) => (
                  <button key={r.id} onClick={() => { setRes(null); setQ(""); nav(key === "reservations" ? base + r.id : base); }}
                    className="block w-full text-left px-2 py-1.5 text-sm rounded hover:bg-white/5">{r.label}</button>
                ))}
              </div>
            )
          ))}
          {!res.reservations?.length && !res.guests?.length && !res.invitations?.length && <p className="text-sm text-muted-foreground px-2 py-2">No results</p>}
        </div>
      )}
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [accessCount, setAccessCount] = useState(0);
  const [referralCount, setReferralCount] = useState(0);
  const [notifCount, setNotifCount] = useState(0);
  React.useEffect(() => {
    const fetchCount = () => {
      api.get("/admin/access-requests/count").then((r) => setAccessCount(r.data.pending || 0)).catch(() => setAccessCount(0));
      api.get("/admin/referrals/count").then((r) => setReferralCount(r.data.pending || 0)).catch(() => setReferralCount(0));
      api.get("/admin/notifications?unread_only=true").then((r) => setNotifCount(r.data.unread || 0)).catch(() => setNotifCount(0));
    };
    fetchCount();
    const t = setInterval(fetchCount, 60000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="min-h-screen bg-ink flex font-sans">
      <aside className={`fixed z-40 h-full w-64 bg-surface/70 border-r border-white/10 flex-col p-4 overflow-y-auto ${open ? "flex" : "hidden"} md:flex`}>
        <Link to="/admin" className="mb-6 flex items-center gap-2"><LogoMark className="h-9 w-9" /><div><div className="font-serif text-gold text-lg leading-none">Command Center</div><div className="text-[9px] tracking-label uppercase text-muted-foreground mt-1">The D</div></div></Link>
        {GROUPS.map((g) => (
          <div key={g.title} className="mb-5">
            <p className="text-[10px] tracking-label uppercase text-muted-foreground/60 px-3 mb-1">{g.title}</p>
            {g.links.filter((l) => !l.ownerOnly || ["owner", "host"].includes(user?.role)).map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)} data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s|&/g, "-")}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${isActive ? "bg-pool/10 text-pool" : "text-muted-foreground hover:text-foreground hover:bg-white/5"}`}>
                <l.icon size={16} /> <span className="flex-1">{l.label}</span>
                {l.to === "/admin/access-requests" && accessCount > 0 && (
                  <span className="ml-auto text-[10px] font-semibold bg-gold text-ink rounded-full px-1.5 min-w-[18px] text-center" data-testid="access-badge">{accessCount}</span>
                )}
                {l.to === "/admin/referrals" && referralCount > 0 && (
                  <span className="ml-auto text-[10px] font-semibold bg-gold text-ink rounded-full px-1.5 min-w-[18px] text-center" data-testid="referral-badge">{referralCount}</span>
                )}
                {l.to === "/admin/my-schedule" && notifCount > 0 && (
                  <span className="ml-auto text-[10px] font-semibold bg-gold text-ink rounded-full px-1.5 min-w-[18px] text-center" data-testid="my-schedule-badge">{notifCount}</span>
                )}
              </NavLink>
            ))}
          </div>
        ))}
        <div className="mt-auto pt-4 border-t border-white/10">
          <button onClick={() => nav("/")} className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground w-full"><Home size={16} /> Public site</button>
          <button onClick={() => { logout(); nav("/"); }} data-testid="admin-logout" className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-red-300 w-full"><LogOut size={16} /> Sign out</button>
        </div>
      </aside>
      <div className="flex-1 md:ml-64">
        <header className="sticky top-0 z-30 bg-ink/80 backdrop-blur border-b border-white/10 px-5 py-3 flex items-center justify-between gap-3">
          <button className="md:hidden text-pool" onClick={() => setOpen((o) => !o)}>{open ? <X /> : <Menu />}</button>
          <div className="hidden sm:block"><SearchBox /></div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground hidden sm:block">{user?.email} · <span className="text-gold">{user?.role}</span></span>
          </div>
        </header>
        <main id="main-content" className="p-5 lg:p-8"><Outlet /></main>
      </div>
      <GhostLauncher mode="admin" />
    </div>
  );
}
